"use client";
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  IconButton,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import {liteClient as algoliasearch} from "algoliasearch/lite";
import {Hits, SearchBox, useInstantSearch} from "react-instantsearch";
import {InstantSearchNext} from "react-instantsearch-nextjs";
import {IoSearch} from "@onemind-services-llc/react-icons-ng/io5";
import {BUTTON_VARIANTS} from "@/theme/components/button";
import {Hit as HitComponent} from "./hit";
import type {PageHit} from "./hit";

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID!,
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY!
);

const INDEX_NAME = "global_pages";

const SearchResults = ({onClose}: {onClose: () => void}) => {
  const {indexUiState, results} = useInstantSearch();
  const query = indexUiState.query || "";

  if (!query) return null;

  return (
    <VStack
      spacing="0"
      align="stretch"
      overflowY="auto"
      maxH="60vh"
      flex="1"
      borderTop="1px solid #eee"
    >
      {results?.nbHits === 0 ? (
        <Box p="2">
          <Text fontWeight="500" color="gray.600">
            No matching results found.
          </Text>
        </Box>
      ) : (
        <Hits<PageHit>
          hitComponent={({hit}) => <HitComponent hit={hit} onClose={onClose} />}
        />
      )}
    </VStack>
  );
};

type SearchBarProps = {isOpen: boolean; onClose: () => void};

const InstantSearchBar = ({isOpen, onClose}: SearchBarProps) => (
  <InstantSearchNext
    searchClient={searchClient}
    indexName={INDEX_NAME}
    insights
    future={{preserveSharedStateOnUnmount: true}}
  >
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside" autoFocus>
      <ModalOverlay bg="rgba(0,0,0,0.4)" />
      <ModalContent
        mt={{base: "10px", md: "5px"}}
        mx="auto"
        w="90%"
        maxW="1000px"
        borderRadius="xl"
        p={{base: "4", md: "6"}}
        boxShadow="2xl"
      >
        <ModalBody p="0">
          <Flex align="center" gap="3" mb="4">
            <IoSearch size="22px" color="#666" />
            <SearchBox
              placeholder="What are you looking for?"
              queryHook={(query, search) => {
                if (query.trim() !== "") {
                  search(query);
                }
              }}
              classNames={{
                root: "searchbox-root",
                input: "searchbox-input",
                submitIcon: "searchbox-hidden-icon",
                resetIcon: "searchbox-hidden-icon",
              }}
            />

            <Button
              variant={BUTTON_VARIANTS.IDEAL}
              fontWeight="500"
              onClick={onClose}
              ml="auto"
            >
              Cancel
            </Button>
          </Flex>
          <SearchResults onClose={onClose} />
        </ModalBody>
      </ModalContent>
    </Modal>
  </InstantSearchNext>
);

export const SearchBar = () => {
  const {isOpen, onOpen, onClose} = useDisclosure();
  return (
    <Box>
      <Flex
        display={{base: "flex", md: "none ", lg: "flex"}}
        direction="row"
        align="center"
        w={{base: "130px", sm: "180px", md: "130px", xl: "150px", "2xl": "180px"}}
        px="3"
        py="2"
        border="1px solid"
        borderColor="gray.300"
        borderRadius="md"
        cursor="pointer"
        _hover={{bg: "gray.50"}}
        onClick={onOpen}
      >
        <IoSearch size="18px" color="#666" style={{marginRight: "8px"}} />
        <Box flex="1" fontSize="md">
          Search...
        </Box>
      </Flex>
      <Box display={{base: "none", md: "flex", lg: "none"}} mr={-2}>
        <IconButton
          icon={<IoSearch size="20px" />}
          aria-label="Search"
          variant="ghost"
          _hover={{bg: "gray.200"}}
          borderRadius="full"
          p="2px"
          onClick={onOpen}
        />
      </Box>
      <InstantSearchBar isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};
